import { Box, Switch, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Label from 'src/components/label';

export type BenefitActivationStatus = 'active' | 'inactive';

export function BenefitStatusHeader() {
  return (
    <Tooltip
      placement="top"
      slotProps={{ tooltip: { sx: { borderRadius: 1 } } }}
      title={
        <Box>
          <Box>
            <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
              Ativo:
            </Box>{' '}
            beneficio ativo e disponível para resgate.
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
              Inativo
            </Box>{' '}
            beneficio desativado manualmente.
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
              Expirado:
            </Box>{' '}
            beneficio fora do período de validade.
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>
              Esgotado:
            </Box>{' '}
            todas as unidades disponíveis ja foram utilizadas.
          </Box>
        </Box>
      }
    >
      <Box component="span">Status</Box>
    </Tooltip>
  );
}

export function BenefitStatusCell({
  value,
  disabled = false,
  ariaLabel = 'Status do benefício',
  onChange,
}: {
  value: unknown;
  disabled?: boolean;
  ariaLabel?: string;
  onChange?: (status: BenefitActivationStatus) => void;
}) {
  const rawStatus = String(value || '')
    .toLowerCase()
    .trim();
  const isDisabled = disabled || !onChange;

  if (rawStatus === 'expired') {
    return (
      <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <Label variant="soft" color="error">
          Expirado
        </Label>
      </Box>
    );
  }

  if (rawStatus === 'soldout' || rawStatus === 'sold_out' || rawStatus === 'sold out') {
    return (
      <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <Label variant="soft" color="warning">
          Esgotado
        </Label>
      </Box>
    );
  }

  if (rawStatus === 'active' || rawStatus === 'inactive') {
    return (
      <Switch
        checked={rawStatus === 'active'}
        inputProps={{ 'aria-label': ariaLabel }}
        disabled={isDisabled}
        onClick={(e) => e.stopPropagation()}
        onChange={(event) => {
          event.stopPropagation();
          if (!isDisabled) onChange?.(event.target.checked ? 'active' : 'inactive');
        }}
        sx={{
          width: 65,
          height: 25,
          p: 0,
          overflow: 'visible',

          '& .MuiSwitch-switchBase': {
            p: '4px',
            transitionDuration: '220ms',

            '&.Mui-checked': {
              transform: 'translateX(35px)',
              color: 'common.white',

              '& + .MuiSwitch-track': {
                borderColor: 'success.main',
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                boxShadow: (theme) =>
                  `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.18)}, 0 8px 18px ${alpha(theme.palette.success.main, 0.24)}`,
              },

              '& + .MuiSwitch-track::before': {
                opacity: 1,
              },

              '& + .MuiSwitch-track::after': {
                opacity: 0,
              },
            },

            '&.Mui-focusVisible .MuiSwitch-thumb': {
              outline: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.24)}`,
              outlineOffset: 2,
            },

            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                borderColor: 'grey.500',
                background: (theme) => theme.palette.grey[500],
                boxShadow: 'none',
                opacity: 1,
              },
              '& .MuiSwitch-thumb': {
                bgcolor: 'grey.100',
                boxShadow: 'none',
              },
            },
          },

          '& .MuiSwitch-thumb': {
            width: 17,
            height: 17,
            bgcolor: 'common.white',
            boxShadow: (theme) =>
              `0 3px 10px ${alpha(theme.palette.grey[900], 0.22)}, inset 0 0 0 1px ${alpha(theme.palette.grey[500], 0.16)}`,
          },

          '& .MuiSwitch-track': {
            position: 'relative',
            borderRadius: 999,
            border: '1px solid',
            borderColor: 'error.main',
            opacity: 1,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.darker} 100%)`,
            boxShadow: (theme) =>
              `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.16)}, 0 8px 18px ${alpha(theme.palette.error.main, 0.2)}`,

            '&::before': {
              content: '"Ativo"',
              position: 'absolute',
              left: 9,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              fontSize: 9,
              lineHeight: 1,
              fontWeight: 700,
              letterSpacing: 0,
              opacity: 0,
              transition: 'opacity 160ms ease',
            },

            '&::after': {
              content: '"Inativo"',
              position: 'absolute',
              right: 7,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              fontSize: 9,
              lineHeight: 1,
              fontWeight: 700,
              letterSpacing: 0,
              opacity: 1,
              transition: 'opacity 160ms ease',
            },
          },
        }}
      />
    );
  }

  return String(value || '');
}
